/**
 * DatewiseSalesReport.jsx — R2
 * Route: /pos/reports/datewise-sales
 * API:   GET /api/pos/reports/datewise-sales
 *
 * Day-aggregated revenue across a date range.
 * Two sub-views toggled via viewMode:
 *   "daily"   — one row per business date
 *   "payment" — payment-mode breakdown per date
 */

import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Popover, Spin, Tabs } from "antd";
import { Download, Filter, Calendar, TrendingUp } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base; //.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const pillStyle = (bg, color) => ({
  background: bg, color, fontWeight: 800, fontSize: 12,
  padding: "5px 10px", borderRadius: 999,
  border: `1px solid ${color}33`, whiteSpace: "nowrap",
});
const downloadCSV = (columns, data, filename) => {
  const h = columns.filter(c => c.dataIndex).map(c => c.title).join(",");
  const r = data.map(row => columns.filter(c => c.dataIndex)
    .map(c => `"${String(row[c.dataIndex] ?? "").replace(/"/g, '""')}"`).join(","));
  const blob = new Blob([[h, ...r].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const DatewiseSalesReport = () => {
  const currency = getCurrency();
  const apiURL   = getApiUrl();

  const [dateRange,      setDateRange]      = useState({ startDate: getBizDate(), endDate: getBizDate() });
  const [activeTab,      setActiveTab]      = useState("daily");
  const [loading,        setLoading]        = useState(false);
  const [daily,          setDaily]          = useState([]);
  const [payment,        setPayment]        = useState([]);
  const [summary,        setSummary]        = useState({});
  const [filterVisible,  setFilterVisible]  = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [exportData,     setExportData]     = useState([]);
  const [exportHeaders,  setExportHeaders]  = useState([]);
  const [filters,        setFilters]        = useState({ order_type: null, is_complementary: null });

  const orderTypeOptions = ["Table Service", "Room Service", "Delivery", "Takeaway", "Counter"]
    .map(v => ({ value: v, label: v }));
  const boolOptions = [{ value: "true", label: "Yes" }, { value: "false", label: "No" }];

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start_date: dateRange.startDate.format("YYYY-MM-DD"),
        end_date:   dateRange.endDate.format("YYYY-MM-DD"),
        ...(filters.order_type       && { order_type:       filters.order_type.value }),
        ...(filters.is_complementary && { is_complementary: filters.is_complementary.value }),
      };
      const res = await axios.get(`${apiURL}/pos/datewise-sales`, {
        headers: { Authorization: `Bearer ${getToken()}` }, params,
      });
      if (res.data.success) {
        setDaily(res.data.daily || []);
        setPayment(res.data.paymentBreakdown || []);
        setSummary(res.data.summary || {});
      }
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters, apiURL]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const dailyColumns = [
    { title: "Date",          dataIndex: "date_label",       width: 130 },
    { title: "Bills",         dataIndex: "bill_count",       width: 80,  align: "right" },
    { title: `Subtotal (${currency})`, dataIndex: "total_subtotal", width: 120, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Tax (${currency})`,   dataIndex: "total_tax",      width: 110, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Discount (${currency})`,dataIndex:"total_discount", width: 110, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Delivery (${currency})`,dataIndex:"total_delivery", width: 110, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Revenue (${currency})`, dataIndex:"total_revenue",  width: 120, align: "right", fixed: "right",
      render: v => <strong>{Number(v||0).toFixed(2)}</strong> },
    { title: `Settled (${currency})`, dataIndex:"settled_amount", width: 120, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Pending (${currency})`, dataIndex:"pending_amount", width: 120, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: `Avg Bill (${currency})`,dataIndex:"avg_bill_value", width: 110, align: "right",
      render: v => Number(v||0).toFixed(2) },
    { title: "Comp Bills",   dataIndex: "complementary_count", width: 90, align: "right" },
  ];

  const paymentColumns = [
    { title: "Date",         dataIndex: "date_label",  width: 130 },
    { title: "Payment Mode", dataIndex: "payment_mode",width: 130 },
    { title: "Transactions", dataIndex: "txn_count",   width: 110, align: "right" },
    { title: `Amount (${currency})`, dataIndex: "amount", width: 120, align: "right",
      render: v => Number(v||0).toFixed(2) },
  ];

  const currentCols = activeTab === "daily" ? dailyColumns : paymentColumns;
  const currentData = activeTab === "daily" ? daily : payment;

  const handleDownloadCSV = () => {
    downloadCSV(currentCols, currentData,
      `Datewise_Sales_${activeTab}_${dateRange.startDate.format("YYYYMMDD")}_${dateRange.endDate.format("YYYYMMDD")}.csv`);
  };

  const handlePrint = () => {
    setExportData(currentData);
    setExportHeaders(currentCols.filter(c=>c.dataIndex).map(c=>({ label: c.title, key: c.dataIndex })));
    setPreviewVisible(true);
  };

  const filterContent = (
    <div style={{ width: 240, display: "grid", gap: 12 }}>
      {[
        { label: "Order Type",    key: "order_type",       options: orderTypeOptions },
        { label: "Complementary", key: "is_complementary", options: boolOptions },
      ].map(f => (
        <div key={f.key}>
          <label style={{ fontSize: 12, fontWeight: 700, display: "block", marginBottom: 4 }}>{f.label}</label>
          <SelectComponent isClearable options={f.options} value={filters[f.key]}
            onChange={v => setFilters(prev => ({ ...prev, [f.key]: v }))}
            styles={{ control: base => ({ ...base, fontSize: 13 }) }} />
        </div>
      ))}
      <Button type="primary" size="small" block onClick={() => { setFilterVisible(false); fetchReport(); }}>
        Apply
      </Button>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="row align-items-center mb-3">
          <div className="col-auto d-flex align-items-center gap-3">
            <h2 style={{ fontSize: 20, fontWeight: 700, borderBottom: "2px solid #007bff", paddingBottom: 4, margin: 0 }}>
              Date-wise Sales Summary
            </h2>
            {/* Date range picker — same component as RoomSoldReport */}
            <DateRangePicker
              key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
              initialSettings={{
                startDate: dateRange.startDate.toDate(),
                endDate:   dateRange.endDate.toDate(),
                ranges: {
                  Today:      [getBizDate().clone().startOf("day").toDate(),           getBizDate().clone().endOf("day").toDate()],
                  Yesterday:  [getBizDate().clone().subtract(1,"days").startOf("day").toDate(), getBizDate().clone().subtract(1,"days").endOf("day").toDate()],
                  "This Week":[getBizDate().clone().startOf("week").toDate(),          getBizDate().clone().endOf("week").toDate()],
                  "This Month":[getBizDate().clone().startOf("month").toDate(),        getBizDate().clone().endOf("month").toDate()],
                  "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(), getBizDate().clone().subtract(1,"month").endOf("month").toDate()],
                },
                locale: { format: "DD MMM YYYY", applyLabel: "Apply", cancelLabel: "Cancel" },
                alwaysShowCalendars: true, autoApply: false,
              }}
              onApply={(_, picker) =>
                setDateRange({ startDate: picker.startDate, endDate: picker.endDate })
              }
            >
              <div className="input-group" style={{ width: 250 }}>
                <input type="text" className="form-control text-center fw-bold"
                  style={{ height: 38, fontSize: 14, borderRight: "none", background: "#fff", width: 220 }}
                  value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`}
                  readOnly />
                <span className="input-group-text" style={{ background: "#fff", borderLeft: "none", height: 38, cursor: "pointer" }}>
                  <Calendar size={15} color="#495057" />
                </span>
              </div>
            </DateRangePicker>
          </div>

          <div className="col-auto ms-auto d-flex align-items-center gap-2">
            {summary.total_bills && (
              <>
                <span style={pillStyle("#eff6ff","#1d4ed8")}>{summary.total_bills} Bills</span>
                <span style={pillStyle("#f0fdf4","#166534")}>{getCurrency()} {Number(summary.total_revenue||0).toFixed(0)}</span>
                <span style={pillStyle("#fef9c3","#854d0e")}>{summary.total_complementary} Comp</span>
              </>
            )}
            <Popover content={filterContent} title="Filters" trigger="click"
              open={filterVisible} onOpenChange={setFilterVisible} placement="bottomRight">
              <Button type="default" className="d-flex align-items-center" style={{ height: 38 }}>
                <Filter size={15} className="me-2" /> Filter
              </Button>
            </Popover>
            <div className="dropdown">
              <Button type="primary" className="d-flex align-items-center dropdown-toggle"
                data-bs-toggle="dropdown" style={{ height: 38 }}>
                <Download size={15} className="me-2" /> Download
              </Button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li><button className="dropdown-item" onClick={handleDownloadCSV}>Download CSV</button></li>
                <li><button className="dropdown-item" onClick={handlePrint}>Print / PDF Preview</button></li>
              </ul>
            </div>
          </div>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginBottom: 12 }}
          items={[
            { key: "daily",   label: "Daily Summary" },
            { key: "payment", label: "Payment Mode Breakdown" },
          ]} />

        {loading ? (
          <div className="text-center my-5"><Spin size="large" /></div>
        ) : (
          <Table columns={currentCols} dataSource={currentData}
            rowKey={(r, i) => r.sort_date || i}
            scroll={{ x: "max-content" }} size="small" bordered
            pagination={{ pageSize: 50, showTotal: t => `${t} records` }} />
        )}

        <PrintReportPreview visible={previewVisible} onClose={() => setPreviewVisible(false)}
          reportTitle={`Date-wise Sales — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
          propertyName="" data={exportData} headers={exportHeaders}
          footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL} />
      </div>
    </div>
  );
};

export default DatewiseSalesReport;
